import {
  candidateTenantsApi,
  propertiesApi,
  type PropertyDto,
  type TenantDto,
} from '../../services/api';
import { ApiError } from '../../services/core.api';
import { onLocaleChange, t } from '../../services/i18n';
import { toastService } from '../../services/toast';
import { confirmDialog } from '../shared/rc-confirm-dialog';
import { openCandidateConvertDialog, openCandidateForm, openCandidateInviteDialog } from './candidate-dialogs';
import { isExpiredConverted } from './candidate-shared';
import './rc-candidate-board-view';
import { RcCandidateBoardView } from './rc-candidate-board-view';

type CandidateEventDetail = {
  id?: string;
  propertyId?: string;
  status?: string;
};

const CANDIDATE_EVENTS = ['add', 'select-property', 'edit', 'status-change', 'delete'] as const;
const ALL_CANDIDATE_EVENTS = [...CANDIDATE_EVENTS, 'invite'] as const;

export class RcCandidateList extends HTMLElement {
  private disposeLocaleChange?: () => void;
  private candidates: TenantDto[] = [];
  private properties: PropertyDto[] = [];
  private activePropertyId: string | null = null;

  async connectedCallback(): Promise<void> {
    ALL_CANDIDATE_EVENTS.forEach((eventName) => this.addEventListener(`candidate-${eventName}`, this.handleCandidateEvent as EventListener));
    this.disposeLocaleChange = onLocaleChange(() => this.render());
    this.innerHTML = `<div class="loading">${t('common.loading')}</div>`;
    await this.loadData();
  }

  disconnectedCallback(): void {
    ALL_CANDIDATE_EVENTS.forEach((eventName) => this.removeEventListener(`candidate-${eventName}`, this.handleCandidateEvent as EventListener));
    this.disposeLocaleChange?.();
  }

  private async loadData(): Promise<void> {
    try {
      [this.candidates, this.properties] = await Promise.all([
        candidateTenantsApi.getAll(),
        propertiesApi.getAll(),
      ]);
      if (this.activePropertyId === null && this.properties.length > 0) {
        this.activePropertyId = this.properties[0].id;
      }
      this.render();
    } catch (error) {
      console.error('Failed to load data:', error);
      this.innerHTML = `<div class="empty-state">${t('waitinglist.loadError')}</div>`;
    }
  }

  private render(): void {
    this.innerHTML = '<rc-candidate-board-view></rc-candidate-board-view>';
    const view = this.querySelector('rc-candidate-board-view') as RcCandidateBoardView | null;
    if (!view) return;
    view.candidates = this.candidates;
    view.properties = this.properties;
    view.activePropertyId = this.activePropertyId;
    view.render();
  }

  private handleCandidateEvent = async (event: Event): Promise<void> => {
    const detail = (event as CustomEvent<CandidateEventDetail>).detail ?? {};
    switch ((event as CustomEvent).type) {
      case 'candidate-add':
        await openCandidateForm({
          activePropertyId: this.activePropertyId,
          properties: this.properties,
          onSaved: async (propertyId) => {
            if (propertyId) this.activePropertyId = propertyId;
            await this.loadData();
          },
        });
        return;
      case 'candidate-select-property':
        if (detail.propertyId) {
          this.activePropertyId = detail.propertyId;
          this.render();
        }
        return;
      case 'candidate-edit': {
        const candidate = this.candidates.find((item) => item.id === detail.id);
        if (!candidate) return;
        await openCandidateForm({
          activePropertyId: this.activePropertyId,
          candidate,
          properties: this.properties,
          onSaved: async () => this.loadData(),
        });
        return;
      }
      case 'candidate-status-change':
        if (detail.id && detail.status) {
          await this.handleStatusChange(detail.id, detail.status);
        }
        return;
      case 'candidate-delete':
        if (detail.id) await this.handleDelete(detail.id);
        return;
      case 'candidate-invite': {
        const candidate = this.candidates.find((item) => item.id === detail.id);
        if (!candidate) return;
        await openCandidateInviteDialog(candidate, async () => this.loadData());
        return;
      }
      default:
        return;
    }
  };

  private async handleStatusChange(id: string, newStatus: string): Promise<void> {
    if (newStatus === 'Actief') {
      const candidate = this.candidates.find((item) => item.id === id);
      if (!candidate) return;
      openCandidateConvertDialog(candidate, async () => this.loadData());
      return;
    }

    const candidate = this.candidates.find((item) => item.id === id);
    if (!candidate) return;

    try {
      await candidateTenantsApi.update(id, {
        tenantId: candidate.tenantId!,
        propertyId: candidate.propertyId,
        interestDate: newStatus === 'Kandidaat'
          ? new Date().toISOString().slice(0, 10)
          : candidate.interestDate!,
        notes: candidate.notes,
        status: newStatus,
      });
      await this.loadData();
    } catch (error) {
      console.error('Failed to update status:', error);
      toastService.error(error instanceof ApiError ? error.message : t('waitinglist.form.saveError'));
    }
  }

  private async handleDelete(id: string): Promise<void> {
    if (!await confirmDialog(t('waitinglist.confirmDelete'))) return;
    try {
      await candidateTenantsApi.delete(id);
      await this.loadData();
    } catch (error) {
      console.error('Failed to delete candidate:', error);
      toastService.error(error instanceof ApiError ? error.message : t('waitinglist.deleteError'));
    }
  }
}

if (!customElements.get('rc-candidate-list')) {
  customElements.define('rc-candidate-list', RcCandidateList);
}
